import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, type Run, type RunStatus } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InfoHint } from '../components/InfoHint';
import { RunAdminActions } from '../components/RunAdminActions';
import { RunProgressBanner } from '../components/RunProgressBanner';
import {
  STAGE_HINTS,
  STAGE_LABELS,
  getRunProgress,
  useRunPolling,
} from '../hooks/useRunPolling';
import { usePersistedTableState } from '../hooks/usePersistedTableState';
import {
  stageDetailsForDisplay,
  stageDisplayName,
  stageErrorForDisplay,
} from '../lib/reportFormat';
import { ReportPage } from './ReportPage';
import { RunLogsTab } from './RunLogsTab';

const STATUS_PILL: Record<RunStatus, string> = {
  CREATED: 'pill-info',
  RUNNING: 'pill-warn',
  SUCCESS: 'pill-success',
  FAILED: 'pill-danger',
};

type RunDetailTab = {
  id: 'progress' | 'logs' | 'attempts' | 'report';
  label: string;
};

const TABS: readonly RunDetailTab[] = [
  { id: 'progress', label: 'Прогон' },
  { id: 'logs', label: 'Журнал' },
  { id: 'attempts', label: 'Попытки' },
  { id: 'report', label: 'Отчёт' },
] as const;

type TabId = RunDetailTab['id'];

function formatMonth(month: string): string {
  return new Date(month).toLocaleDateString('ru-RU', {
    month: 'long',
    year: 'numeric',
  });
}

function runSummary(params: Record<string, unknown> | null): string {
  if (!params) return '—';
  const cats = (params.categories_scope as string[] | undefined)?.length ?? 0;
  const ai = params.llm_enabled ? 'AI' : 'без AI';
  const detail = Number(params.bhapi_top_n ?? 0);
  return `${cats} кат., ${ai}, дет. ${detail}`;
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}

function compareNullableNumber(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: 'asc' | 'desc',
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return direction === 'asc' ? a - b : b - a;
}

function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: 'asc' | 'desc',
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const result = a.localeCompare(b, 'ru');
  return direction === 'asc' ? result : -result;
}

function statFrom(
  stats: Record<string, unknown> | null,
  path: string[],
): unknown {
  let current: unknown = stats;
  for (const key of path) {
    if (!current || typeof current !== 'object' || !(key in current))
      return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function StatCard({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="card card-padding">
      <div className="rubric">{label}</div>
      <div className="text-h3 text-text-primary font-strong mt-1">
        {value == null ? '—' : String(value)}
      </div>
    </div>
  );
}

function money(value: unknown): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n).toLocaleString('ru-RU')} ₽`;
}

function RunSettings({ run, isAdmin }: { run: Run; isAdmin: boolean }) {
  const params = run.params ?? {};
  const categories = (params.categories_scope as string[] | undefined) ?? [];
  const thresholds =
    (params.thresholds as Record<string, unknown> | undefined) ?? {};
  const summaryItems = [
    {
      label: 'AI-комментарии',
      value: params.llm_enabled ? 'Включены' : 'Отключены',
      description: 'Комментарии и разделы 4–7; не меняют отбор событий.',
    },
    {
      label: 'Детализация карточек',
      value: Number(params.bhapi_top_n ?? 0),
      description: 'TOP-N уже найденных карточек разделов 1–2.',
    },
    {
      label: 'Категории',
      value: categories.length,
      description: 'Область данных всех разделов отчёта.',
    },
  ];

  return (
    <section className="card card-padding space-y-4">
      <div className="inline-flex items-center gap-2">
        <div className="rubric">Настройки запуска</div>
        <InfoHint text="Это снимок параметров конкретного прогона. Дальнейшие изменения дефолтов не переписывают эти значения." />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {summaryItems.map((item) => (
          <div key={item.label} className="rounded-md bg-surface-2 p-3">
            <div className="rubric">{item.label}</div>
            <div className="text-h3 text-text-primary font-strong mt-1">
              {String(item.value)}
            </div>
            <div className="hint mt-1">{item.description}</div>
          </div>
        ))}
      </div>
      <div className="rounded-md bg-surface-2 p-3 text-caption text-text-secondary">
        Пороги ниже применяются только к карточкам разделов 1–2. Раздел 3 сравнивает отдельные агрегаты брендов и продавцов выбранного и предыдущего месяца.
      </div>
      <div className="grid md:grid-cols-3 gap-3 text-caption">
        <div>
          <div className="text-text-tertiary">Раздел 1 · заказы от</div>
          <div className="text-text-primary">
            {money(thresholds.new_winner_min_revenue)}
          </div>
          <div className="hint mt-1">Минимум заказов новой карточки за месяц.</div>
        </div>
        <div>
          <div className="text-text-tertiary">Раздел 2 · порог роста</div>
          <div className="text-text-primary">
            {thresholds.growth_rate == null
              ? '—'
              : `${Number(thresholds.growth_rate) * 100}%`}
          </div>
          <div className="hint mt-1">Рост должен быть строго выше этого значения.</div>
        </div>
        <div>
          <div className="text-text-tertiary">Раздел 2 · заказы прошлого месяца от</div>
          <div className="text-text-primary">
            {money(thresholds.min_prev_revenue)}
          </div>
          <div className="hint mt-1">Отсекает рост с низкой базы.</div>
        </div>
      </div>
      {categories.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {categories.map((path) => (
            <span key={path} className="pill-neutral mono">
              {path}
            </span>
          ))}
        </div>
      ) : null}
      {isAdmin ? (
        <details className="text-caption text-text-secondary">
          <summary className="cursor-pointer text-text-tertiary">
            Технический JSON
          </summary>
          <pre className="mt-2 mono overflow-auto max-h-48 bg-surface-2 rounded-md p-3">
            {JSON.stringify(params, null, 2)}
          </pre>
        </details>
      ) : null}
    </section>
  );
}

type AttemptsSortBy =
  | 'attempt_no'
  | 'status'
  | 'started_at'
  | 'finished_at'
  | 'stage'
  | 'new_winners'
  | 'hyper_growth'
  | 'error';

type AttemptsTableState = {
  sortBy: AttemptsSortBy;
  sortDir: 'asc' | 'desc';
};

const ATTEMPTS_TABLE_DEFAULTS: AttemptsTableState = {
  sortBy: 'attempt_no',
  sortDir: 'desc',
};
const ATTEMPTS_TABLE_SORTING_KEYS: readonly (keyof AttemptsTableState)[] = [
  'sortBy',
  'sortDir',
];

function AttemptsTable({
  run,
  user,
}: {
  run: Run;
  user: ReturnType<typeof useAuth>['user'];
}) {
  const attempts = run.attempts ?? [];
  const [tableState, setTableState] = usePersistedTableState(
    user?.id,
    `run.${run.id}.attempts`,
    ATTEMPTS_TABLE_DEFAULTS,
    {
      sortingKeys: ATTEMPTS_TABLE_SORTING_KEYS,
      persistSorting: Boolean(user?.persist_table_sorting),
      persistFilters: Boolean(user?.persist_table_filters),
    },
  );
  const { sortBy, sortDir } = tableState;

  function updateSort(key: AttemptsSortBy) {
    if (sortBy === key) {
      setTableState((prev) => ({
        ...prev,
        sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      setTableState((prev) => ({ ...prev, sortBy: key, sortDir: 'asc' }));
    }
  }

  function SortHeader({
    label,
    value,
  }: {
    label: string;
    value: AttemptsSortBy;
  }) {
    const active = sortBy === value;
    return (
      <button
        type="button"
        className="text-caption text-text-secondary hover:text-text-primary"
        onClick={() => updateSort(value)}
      >
        {label}
        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    );
  }

  if (attempts.length === 0) {
    return (
      <div className="card card-padding text-text-tertiary">
        Попыток ещё нет.
      </div>
    );
  }
  const rows = attempts
    .map((a) => {
      const stats = a.stats as {
        current_stage?: string;
        analytics?: { new_winners?: number; hyper_growth?: number };
      } | null;
      const stage = stats?.current_stage
        ? (STAGE_LABELS[stats.current_stage] ?? stats.current_stage)
        : '—';
      const error = stageErrorForDisplay(stats?.current_stage, a.error) ?? '—';
      return { attempt: a, stats, stage, error };
    })
    .sort((a, b) => {
      if (sortBy === 'attempt_no') {
        return compareNullableNumber(
          a.attempt.attempt_no,
          b.attempt.attempt_no,
          sortDir,
        );
      }
      if (sortBy === 'status')
        return compareText(a.attempt.status, b.attempt.status, sortDir);
      if (sortBy === 'started_at') {
        return compareNullableNumber(
          a.attempt.started_at
            ? new Date(a.attempt.started_at).getTime()
            : null,
          b.attempt.started_at
            ? new Date(b.attempt.started_at).getTime()
            : null,
          sortDir,
        );
      }
      if (sortBy === 'finished_at') {
        return compareNullableNumber(
          a.attempt.finished_at
            ? new Date(a.attempt.finished_at).getTime()
            : null,
          b.attempt.finished_at
            ? new Date(b.attempt.finished_at).getTime()
            : null,
          sortDir,
        );
      }
      if (sortBy === 'stage') return compareText(a.stage, b.stage, sortDir);
      if (sortBy === 'new_winners') {
        return compareNullableNumber(
          a.stats?.analytics?.new_winners,
          b.stats?.analytics?.new_winners,
          sortDir,
        );
      }
      if (sortBy === 'hyper_growth') {
        return compareNullableNumber(
          a.stats?.analytics?.hyper_growth,
          b.stats?.analytics?.hyper_growth,
          sortDir,
        );
      }
      return compareText(a.error, b.error, sortDir);
    });

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>
              <SortHeader label="№" value="attempt_no" />
            </th>
            <th>
              <SortHeader label="Статус" value="status" />
            </th>
            <th>
              <SortHeader label="Старт" value="started_at" />
            </th>
            <th>
              <SortHeader label="Финиш" value="finished_at" />
            </th>
            <th>
              <SortHeader label="Этап" value="stage" />
            </th>
            <th>
              <SortHeader label="Новые" value="new_winners" />
            </th>
            <th>
              <SortHeader label="Гипер-рост" value="hyper_growth" />
            </th>
            <th>
              <SortHeader label="Ошибка" value="error" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ attempt: a, stats, stage, error }) => {
            return (
              <tr key={a.id}>
                <td className="mono text-text-primary">{a.attempt_no}</td>
                <td>
                  <span className={STATUS_PILL[a.status]}>{a.status}</span>
                </td>
                <td className="text-caption">
                  {formatDateTime(a.started_at ?? undefined)}
                </td>
                <td className="text-caption">
                  {formatDateTime(a.finished_at ?? undefined)}
                </td>
                <td className="text-caption text-text-secondary">{stage}</td>
                <td className="mono text-text-primary">
                  {stats?.analytics?.new_winners ?? '—'}
                </td>
                <td className="mono text-text-primary">
                  {stats?.analytics?.hyper_growth ?? '—'}
                </td>
                <td className="text-caption text-danger max-w-[260px] truncate">
                  {error}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function RunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = (searchParams.get('tab') as TabId) || 'progress';
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const tab = TABS.some((t) => t.id === requestedTab)
    ? requestedTab
    : 'progress';

  const [run, setRun] = useState<Run | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRun = useCallback(() => {
    if (!runId) return;
    api
      .get<Run>(`/api/runs/${runId}`)
      .then(setRun)
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : String(err)),
      );
  }, [runId]);

  useEffect(() => {
    loadRun();
  }, [loadRun]);

  const polled = useRunPolling(runId, run);
  const activeRun = polled ?? run;
  const progress = getRunProgress(activeRun);

  useEffect(() => {
    if (requestedTab !== tab) {
      setSearchParams({ tab }, { replace: true });
    }
  }, [requestedTab, setSearchParams, tab]);

  if (!runId) {
    return <div className="error-hint">Не указан id прогона</div>;
  }

  if (error) {
    return (
      <div className="flex flex-col gap-3">
        <Link to="/" className="btn-ghost btn-sm self-start">
          ← К списку
        </Link>
        <div className="error-hint">{error}</div>
      </div>
    );
  }

  if (!activeRun) {
    return <div className="text-text-tertiary">Загрузка…</div>;
  }

  const rawStages = (activeRun.stats as { stages?: unknown } | null)?.stages;
  const stageList = Array.isArray(rawStages)
    ? (rawStages as Array<Record<string, unknown>>)
    : null;
  const stats = activeRun.stats;
  const stageLabel = progress.current_stage
    ? (STAGE_LABELS[progress.current_stage] ?? progress.current_stage)
    : null;
  const runActions = activeRun.actions ?? {
    can_retry: false,
    can_delete: false,
    can_edit: false,
    can_cancel: false,
  };
  const hasRunActions =
    runActions.can_retry ||
    runActions.can_delete ||
    runActions.can_edit ||
    runActions.can_cancel;

  return (
    <div className="flex flex-col gap-6">
      <div className="section-header page-header">
        <div>
          <div className="rubric">Прогон</div>
          <h1 className="inline-flex items-center gap-2 text-h1 font-strong text-text-primary">
            {activeRun.title}
            <InfoHint text="Карточка прогона показывает статус, текущий этап, журнал, попытки и итоговый отчёт." />
          </h1>
          <div className="text-caption text-text-tertiary mt-1">
            {formatMonth(activeRun.month)} · {activeRun.marketplace}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span
              className={STATUS_PILL[activeRun.status]}
              title={progress.current_detail ?? ''}
            >
              {activeRun.status}
            </span>
            {stageLabel ? (
              <span className="pill-neutral">{stageLabel}</span>
            ) : null}
            <span className="text-caption text-text-tertiary">
              {runSummary(activeRun.params)}
            </span>
            <span className="text-caption text-text-tertiary mono">
              id {activeRun.id.slice(0, 8)}
            </span>
          </div>
        </div>
        <Link to="/" className="btn-ghost btn-md">
          ← К списку
        </Link>
      </div>

      <div className="flex gap-2 border-b border-border-subtle">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`px-3 py-2 text-label border-b-2 -mb-px ${
              tab === t.id
                ? 'border-accent text-text-primary'
                : 'border-transparent text-text-tertiary hover:text-text-secondary'
            }`}
            onClick={() => setSearchParams({ tab: t.id })}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'progress' ? (
        <div className="flex flex-col gap-4">
          <RunProgressBanner run={activeRun} />
          {hasRunActions ? (
            <RunAdminActions run={activeRun} onUpdated={loadRun} />
          ) : null}
          {activeRun.error ? (
            <div className="card card-padding border-danger/30">
              <div className="rubric">Ошибка</div>
              <div className="mono text-caption text-danger whitespace-pre-wrap break-all">
                {stageErrorForDisplay(
                  progress.current_stage,
                  activeRun.error,
                ) ?? activeRun.error}
              </div>
            </div>
          ) : null}
          <div className="grid md:grid-cols-4 gap-3">
            <StatCard
              label="Категории"
              value={
                (
                  (activeRun.params?.categories_scope as
                    | string[]
                    | undefined) ?? []
                ).length
              }
            />
            <StatCard
              label="SKU проверено"
              value={statFrom(stats, ['analytics', 'total_skus_examined'])}
            />
            <StatCard
              label="NEW-WINNER"
              value={statFrom(stats, ['analytics', 'new_winners'])}
            />
            <StatCard
              label="Гиперрост"
              value={statFrom(stats, ['analytics', 'hyper_growth'])}
            />
          </div>
          {progress.last_heartbeat_at ? (
            <div className="card card-padding text-caption text-text-secondary">
              Последний heartbeat: {formatDateTime(progress.last_heartbeat_at)}
              {progress.current_detail ? (
                <span className="mono text-text-tertiary">
                  {' '}
                  · {progress.current_detail}
                </span>
              ) : null}
            </div>
          ) : null}
          <RunSettings run={activeRun} isAdmin={isAdmin} />
          {stageList && stageList.length > 0 ? (
            <section className="card card-padding flex flex-col gap-3">
              <div className="rubric mb-2">Стадии pipeline</div>
              <ul className="space-y-3 text-caption">
                {stageList.map((s, i) => {
                  const rawStage = typeof s.name === 'string' ? s.name : null;
                  const safeError = stageErrorForDisplay(
                    rawStage,
                    typeof s.error === 'string' ? s.error : null,
                  );
                  const safeDetails = stageDetailsForDisplay(
                    rawStage,
                    s.details &&
                      typeof s.details === 'object' &&
                      !Array.isArray(s.details)
                      ? (s.details as Record<string, unknown>)
                      : null,
                  );
                  return (
                    <li
                      key={String(s.name ?? i)}
                      className="border-b border-border-subtle pb-3 last:border-b-0"
                    >
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-text-secondary">
                          {stageDisplayName(rawStage)}
                        </span>
                        <span
                          className={
                            s.status === 'success'
                              ? 'pill-success'
                              : s.status === 'failed'
                                ? 'pill-danger'
                                : s.status === 'running'
                                  ? 'pill-warn'
                                  : 'pill-neutral'
                          }
                        >
                          {String(s.status)}
                        </span>
                        {s.duration_sec != null ? (
                          <span className="text-text-tertiary">
                            {String(s.duration_sec)}s
                          </span>
                        ) : null}
                        {s.items_in != null ? (
                          <span className="text-text-tertiary">
                            вход: {String(s.items_in)}
                          </span>
                        ) : null}
                        {s.items_out != null ? (
                          <span className="text-text-tertiary">
                            выход: {String(s.items_out)}
                          </span>
                        ) : null}
                      </div>
                      {rawStage && STAGE_HINTS[rawStage] ? (
                        <div className="mt-2 text-text-secondary">
                          <div>{STAGE_HINTS[rawStage].what}</div>
                          <div className="text-text-tertiary">
                            {STAGE_HINTS[rawStage].why}
                          </div>
                        </div>
                      ) : null}
                      {safeError ? (
                        <div className="error-hint mt-2">{safeError}</div>
                      ) : null}
                      {safeDetails && Object.keys(safeDetails).length > 0 ? (
                        <pre className="mono text-caption text-text-secondary overflow-auto max-h-48 mt-2 bg-surface-2 rounded-md p-3">
                          {JSON.stringify(safeDetails, null, 2)}
                        </pre>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}

      {tab === 'logs' ? <RunLogsTab runId={runId} run={activeRun} /> : null}

      {tab === 'attempts' ? (
        <AttemptsTable run={activeRun} user={user} />
      ) : null}

      {tab === 'report' ? <ReportPage sectionsOnly runId={runId} /> : null}
    </div>
  );
}
